import Bowser from 'bowser';
import { intersection } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import {
  fetchManagedGroupDetails,
  fetchOpenGroupDetails,
  fetchQuestionnaireDetails,
} from '../../store/actions/questionnaireDetailsActions';
import { useQuery } from '../../utils/hooks/useQuery';
import { LocalStorageService } from '../../utils/LocalStorage.service';
import { PreTest } from '../pages/PreTest';
import { Test } from '../pages/Test';
import { BlockMobileScreen } from '../shared/BlockMobileScreen';
import { Error } from '../shared/Error';
import { Spinner } from '../shared/Spinner';
import { desktopOnlyTest, phoneLandscapeTests } from './constants';

import ManagedGroupPlayer from '../shared/ManagedGroup/ManagedGroupPlayer';
import OpenGroupPlayer from '../shared/OpenGroup/OpenGroupPlayer';
import { useOrientation } from '../shared/utils/isLandscape';

const QuestionnairePlayer = ({ isStarted, isBattery, isFirstInBattery, is360, areTermsSkipped, isLandscapeOnly }) => {
  const { t } = useTranslation();

  const [isPreTestCompleted, setPreTestCompleted] = React.useState(isStarted || (isBattery && !isFirstInBattery));

  const onComplete = React.useCallback(() => {
    setPreTestCompleted(true);
  }, [setPreTestCompleted]);

  return React.useMemo(() => {
    switch (true) {
      case !isPreTestCompleted:
        return <PreTest onComplete={onComplete} is360={is360} areTermsSkipped={areTermsSkipped} />;
      case !!isPreTestCompleted:
        return <Test isLandscapeOnly={isLandscapeOnly} is360={is360} />;
      default:
        return <Error>{t('errorQuestionnairePlayer')}</Error>;
    }
  }, [is360, isPreTestCompleted, onComplete, t]);
};

const QuestionnaireComponent = ({
  match,
  history,
  fetchQuestionnaireDetails,
  fetchManagedGroupDetails,
  fetchOpenGroupDetails,
  isFetching,
  isError,
  error,
  isStarted,
  activeQuestion,
  isFirstInBattery,
  isBattery,
  testId,
  dependencies,
  is360,
  isManagedGroup,
  isOpenGroup,
  skipPrivacyPolicy,
}) => {
  const { t } = useTranslation();
  const { accountWithInvitation, password, accessCode } = match.params;
  const query = useQuery();
  const mobileOrientation = useOrientation();

  useEffect(() => {
    const token = query.get('token');
    if (token) {
      LocalStorageService.saveToken(token);
      history.replace(window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!isManagedGroup && !isOpenGroup) fetchQuestionnaireDetails(accountWithInvitation, password, is360);
  }, [accountWithInvitation, password, fetchQuestionnaireDetails, is360, isManagedGroup, isOpenGroup]);

  useEffect(() => {
    if (isManagedGroup) fetchManagedGroupDetails(accessCode);
    if (isOpenGroup) fetchOpenGroupDetails(accessCode);
  }, []);

  const PlayerComponent = useMemo(() => {
    if (isManagedGroup) return ManagedGroupPlayer;
    if (isOpenGroup) return OpenGroupPlayer;
    return QuestionnairePlayer;
  }, [isManagedGroup, isOpenGroup]);

  if (isFetching) return <Spinner text={t('loadingSessionDetails')} />;
  if (isError)
    return (
      <Error
        title={t('invalidInvitation')}
        onNext={() => {
          history.push(`/`);
        }}
      >
        {error.message === '' ? t('SomethingIsNotRightPleasCheckYourInvitation') : error.message}
      </Error>
    );

  const browser = Bowser.getParser(window.navigator.userAgent);
  const platform = browser.getPlatform();
  const isMobile = platform.type === 'mobile';

  const desktopBlocker = desktopOnlyTest.includes(testId) || intersection(desktopOnlyTest, dependencies).length > 0;
  const isLandscapeOnly = phoneLandscapeTests.includes(testId);

  const showMobileBlockingScreen = isMobile && (desktopBlocker || ( isLandscapeOnly && mobileOrientation === 'portrait'));

  const blockingType = desktopBlocker ? "desktop" : "landscape";

  return showMobileBlockingScreen ? (
    <BlockMobileScreen blockerType={blockingType} />
  ) : (
    <PlayerComponent
      isLandscapeOnly={isLandscapeOnly}
      isStarted={isStarted}
      activeQuestion={activeQuestion}
      isFirstInBattery={isFirstInBattery}
      isBattery={isBattery}
      is360={is360}
      isManagedGroup={isManagedGroup}
      areTermsSkipped={skipPrivacyPolicy}
    />
  );
};

const mapStateToProps = (state) => {
  const {
    questionnaireDetails: {
      isFetching,
      isError,
      error,
      isStarted,
      session: { testId, batteryDependencies = '', batteryId = -1 } = {},
      details: { skipPrivacyPolicy } = {},
      openGroup,
      managedGroup,
    },
  } = state;
  const resolvedTestId = openGroup?.testID || managedGroup.sessionData?.testID || testId;
  const resolvedBatteryId = openGroup?.batteryID || managedGroup.sessionData?.batteryID || batteryId;
  const resolvedBatteryDependencies =
    openGroup?.batteryDependencies || managedGroup.sessionData?.batteryDependencies || batteryDependencies;

  const isFirstInBattery =
    typeof resolvedBatteryDependencies === 'string' && resolvedBatteryDependencies.split(';')[0] === resolvedTestId;
  return {
    isFetching,
    isError,
    error,
    isStarted,
    isFirstInBattery,
    isBattery: resolvedBatteryId && resolvedBatteryId !== -1,
    testId: resolvedTestId,
    dependencies: resolvedBatteryDependencies.split(';'),
    skipPrivacyPolicy,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchQuestionnaireDetails: (accountWithInvitation, password, is360) => {
      return dispatch(fetchQuestionnaireDetails(accountWithInvitation, password, is360));
    },
    fetchManagedGroupDetails: (accessCode) => {
      return dispatch(fetchManagedGroupDetails(accessCode));
    },
    fetchOpenGroupDetails: (accessCode) => {
      return dispatch(fetchOpenGroupDetails(accessCode));
    },
  };
};

export const Questionnaire = compose(connect(mapStateToProps, mapDispatchToProps), withRouter)(QuestionnaireComponent);
